import { inject, Injectable } from '@angular/core';
import { DbService } from '../db/db.service';
import { AssignedCategory } from '../../models/assignedCategory';
import { User } from '../../models/user';
import { AssignedClient } from '../../models/assignedClient';
import { Category } from '../../models/category';
import { isNoEmptyArray } from '@pineapplelab/util';
import { PublishStatusEnum } from '../../enums/publishStatus.enum';
import firebase from 'firebase/compat/app';

@Injectable({
  providedIn: 'root'
})
export class AssignedCategoryService {

  private db = inject(DbService);

  // The client (parent account) that owns a learner's assigned categories never
  // changes during a session, so its document is resolved once per username and
  // shared by every per-category lookup instead of re-running the users query.
  private clientRefCache = new Map<string, Promise<firebase.firestore.DocumentReference | null>>();

  getClientUserRef(username: string, projectId: string): Promise<firebase.firestore.DocumentReference | null> {
    const key = `${projectId}/${username}`;
    let cached = this.clientRefCache.get(key);
    if (!cached) {
      cached = this.db.getProyIdRef(projectId)
        .collection(User.PATH)
        .where('username', '==', username)
        .limit(1)
        .get()
        .then(usersSnap => usersSnap.empty ? null : usersSnap.docs[0].ref)
        .catch(error => {
          // Don't memoize a failed lookup; the next call retries it.
          this.clientRefCache.delete(key);
          throw error;
        });
      this.clientRefCache.set(key, cached);
    }
    return cached;
  }

  async getAssignedCategoryByClient(username: string, categoryId: string, projectId: string): Promise<AssignedCategory | null> {
    const clientRef = await this.getClientUserRef(username, projectId);

    if (!clientRef) {
      return null;
    }

    const assignedCategorySnap = await clientRef
      .collection(AssignedCategory.PATH)
      .doc(categoryId)
      .get();

    if (assignedCategorySnap.exists) {
      return new AssignedCategory(assignedCategorySnap.data());
    }

    return null;
  }

  async getCurrentAssignedCategory(userId: string, categoryId: string, projectId: string): Promise<AssignedCategory> {
    const assignedCategorySnap = await this.db.getProyIdRef(projectId)
      .collection(User.PATH)
      .doc(userId)
      .collection(AssignedCategory.PATH)
      .doc(categoryId)
      .get();

    if (assignedCategorySnap.exists) {
      return new AssignedCategory(assignedCategorySnap.data());
    }

    return null;
  }

  async getAssignedClientsOnCategory(categoryId: string, projectId: string): Promise<User[]> {
    const assignedClientSnap = await this.db.getProyIdRef(projectId)
      .collection(Category.PATH)
      .doc(categoryId)
      .collection(AssignedClient.PATH)
      .get();

    if (assignedClientSnap.empty) {
      return [];
    }
    const assignedClients = assignedClientSnap.docs.map(doc => new AssignedClient(doc.data()));
    const userFetches = assignedClients.map(async client => {
      if (!client.reference) return null;

      try {
        const userSnap = await client.reference.get();
        return userSnap.exists ? new User(userSnap.data()) : null;
      } catch (error) {
        console.error('Error fetching user:', error);
        return null;
      }
    });
    const users = await Promise.all(userFetches);
    return users.filter((user): user is User => user !== null);
  }


  async addAssignedCategory(assignedCategory: AssignedCategory | AssignedCategory[], projectId: string, userId: string): Promise<void> {
    if (!assignedCategory) return;

    const assignedCategories = Array.isArray(assignedCategory)
      ? assignedCategory
      : [assignedCategory];

    for (const category of assignedCategories) {
      if (!category.id) continue;

      const assignedCategoryRef = this.db.getProyIdRef(projectId)
        .collection(User.PATH)
        .doc(userId)
        .collection(AssignedCategory.PATH)
        .doc(category.id);

      await assignedCategoryRef.set(category.toJson());
    }
  }

  async setAssignedCategoriesOnClient(categories: AssignedCategory[], projectId: string, userId: string): Promise<void> {
    if (!isNoEmptyArray(categories)) return;

    const batch = this.db.firestore.batch();

    for (const category of categories) {
      const assignedCategory = new AssignedCategory(category);
      const assignedCategoryRef = this.db.getProyIdRef(projectId)
        .collection(User.PATH)
        .doc(userId)
        .collection(AssignedCategory.PATH)
        .doc(assignedCategory.id);

      batch.set(assignedCategoryRef, assignedCategory.toJson());
    }

    await batch.commit();
  }

  	async getCategoriesAssigned(projectId: string, authUserId: string): Promise<Category[]> {
		const assignedCategoriesSnap = await this.db.getProyIdRef(projectId)
			.collection(User.PATH)
			.doc(authUserId)
			.collection(AssignedCategory.PATH)
			.get();

		if (assignedCategoriesSnap.empty) {
			return [];
		}

		const assignedCategoryDocs = assignedCategoriesSnap.docs.map(doc => new AssignedCategory(doc.data()));
		const categoryRefs = assignedCategoryDocs.map(ac => ac.category).filter(Boolean);
		const categorySnaps = await Promise.all(categoryRefs.map(ref => ref.get()));

		return categorySnaps
			.filter(snap => snap.exists)
			.map(snap => new Category(snap.data()))
			.filter(category => category?.status === PublishStatusEnum.PUBLISHED);
	}


	async getAssignedCategories(projectId: string, userId: string): Promise<AssignedCategory[]> {
		const assignedCategoriesSnap = await this.db.getProyIdRef(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(AssignedCategory.PATH)
			.get();

		if (assignedCategoriesSnap.empty) {
			return [];
		}

		return assignedCategoriesSnap.docs
			.map(doc => new AssignedCategory(doc.data()))
			.filter(ac => !!ac?.category);
	}
}
